import { useState, useRef, KeyboardEvent, forwardRef, useImperativeHandle } from "react"
import { PaperPlaneTilt, Stop } from "@phosphor-icons/react"
import { Button } from "../../../components/ui/button"
import { cn } from "../../../lib/utils"

interface GlobalAIInputProps {
  onSend: (content: string) => void
  onCancel?: () => void
  isStreaming?: boolean
  disabled?: boolean
  placeholder?: string
}

export interface GlobalAIInputHandle {
  focus: () => void
}

export const GlobalAIInput = forwardRef<GlobalAIInputHandle, GlobalAIInputProps>(
  ({ onSend, onCancel, isStreaming = false, disabled = false, placeholder = "Type a message..." }, ref) => {
    const [value, setValue] = useState("")
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    useImperativeHandle(ref, () => ({
      focus: () => {
        textareaRef.current?.focus()
      },
    }))

    const handleSend = () => {
      const trimmed = value.trim()
      if (!trimmed || disabled) return

      onSend(trimmed)
      setValue("")

      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto"
      }
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    }

    const handleInput = () => {
      const textarea = textareaRef.current
      if (textarea) {
        textarea.style.height = "auto"
        textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px"
      }
    }

    return (
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            className={cn(
              "w-full resize-none rounded-lg border border-border",
              "bg-background px-3 pt-[7px] pb-[5px] text-sm leading-5",
              "placeholder:text-muted-foreground",
              "focus:outline-none focus:ring-2 focus:ring-primary/50",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              "max-h-[120px]"
            )}
          />
        </div>
        <Button
          size="icon"
          onClick={isStreaming ? onCancel : handleSend}
          disabled={!isStreaming && (disabled || !value.trim())}
          variant={isStreaming ? "danger" : "default"}
          className="h-[34px] w-[34px] shrink-0 -mt-[5px]"
        >
          {isStreaming ? (
            <Stop className="h-4 w-4" weight="fill" />
          ) : (
            <PaperPlaneTilt className="h-4 w-4" />
          )}
        </Button>
      </div>
    )
  }
)
